/**
 * Catalog context for page components (M6 performance).
 *
 * Route loaders fetch a {@link CatalogSnapshot} from the server
 * (`loadPageData`); `CatalogProvider` turns it into the regular `Catalog` API.
 * Components call `useCatalog()` instead of importing the full catalog, so no
 * content ships inside the JS bundle. Client-safe: type-only content imports.
 */
import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { assembleCatalog } from './catalog-api'
import type { Catalog } from './catalog-api'
import type { LegalDocument } from './legal/types'
import type { CatalogSnapshot } from './page-data'

export type { CatalogSnapshot }

export function catalogFromSnapshot(snapshot: CatalogSnapshot): Catalog {
  return assembleCatalog(snapshot.locale, {
    courses: snapshot.courses ?? [],
    lessons: snapshot.lessons ?? [],
    resources: snapshot.resources ?? [],
    articles: snapshot.articles ?? [],
    fundamentals: snapshot.fundamentals ?? [],
    builds: snapshot.builds ?? [],
    glossaryTerms: snapshot.glossaryTerms ?? [],
    comparisons: snapshot.comparisons ?? [],
    guides: snapshot.guides ?? [],
    tools: snapshot.tools ?? [],
    communityTopics: snapshot.communityTopics ?? [],
    communityPosts: snapshot.communityPosts ?? [],
    changelogEntries: snapshot.changelogEntries ?? [],
  })
}

type CatalogContextValue = { catalog: Catalog; legal?: LegalDocument }

const CatalogContext = createContext<CatalogContextValue | null>(null)

export function CatalogProvider({
  snapshot,
  children,
}: {
  snapshot: CatalogSnapshot
  children: ReactNode
}) {
  const value = useMemo(
    () => ({ catalog: catalogFromSnapshot(snapshot), legal: snapshot.legal }),
    [snapshot],
  )
  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
}

/** Test/SSR helper: provide an already-assembled catalog (e.g. the full one). */
export function CatalogValueProvider({
  catalog,
  legal,
  children,
}: {
  catalog: Catalog
  legal?: LegalDocument
  children: ReactNode
}) {
  const value = useMemo(() => ({ catalog, legal }), [catalog, legal])
  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
}

function useCatalogContext(): CatalogContextValue {
  const value = useContext(CatalogContext)
  if (!value) {
    throw new Error(
      'useCatalog() needs a <CatalogProvider>: give the route a `loadPageData` loader.',
    )
  }
  return value
}

/** The page's catalog slice (same API as `getCatalog(locale)`). */
export function useCatalog(): Catalog {
  return useCatalogContext().catalog
}

/** The legal document a legal route's loader sent down. */
export function useLegalDocument(): LegalDocument {
  const legal = useCatalogContext().legal
  if (!legal) throw new Error('useLegalDocument() on a route without legal data')
  return legal
}
